import type { ReactNode, TextareaHTMLAttributes } from 'react';
import { useId } from 'react';
import { cn } from './cn.js';
import { useRequiredMark } from './Form.js';
import { Icon } from './Icon.js';

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  error?: string | null;
  help?: ReactNode;
  /** Mostra "0/2000" abaixo do campo. Só quando há `maxLength`. */
  counter?: boolean;
  /**
   * O campo sem moldura própria, para viver DENTRO de uma caixa que já tem uma.
   *
   * É o compositor de conversa do desenho de 05/09: uma caixa só, com o texto
   * em cima e a barra de ações embaixo. Com a moldura do campo ali dentro
   * ficariam duas bordas concêntricas separadas por 12px de nada.
   *
   * O rótulo **não some, fica invisível** (`sr-only`). O desenho põe só um
   * `placeholder`, e placeholder não é rótulo: ele desaparece na primeira
   * tecla, não é lido como nome do campo por leitor de tela nenhum, e some da
   * tela justamente para quem voltou depois de uma interrupção. Quem monta a
   * caixa em volta é que precisa dizer o que ela é.
   */
  bare?: boolean;
}

/**
 * Texto de mais de uma linha.
 *
 * Sobe para `packages/ui` pela regra do `PADROES.md`: promover quando a
 * terceira tela precisar. Demandas trouxe cinco de uma vez — abrir demanda na
 * interna, responder, nota interna, abrir no portal e responder no portal — e
 * escrever `<textarea className="...">` em cada uma é exatamente como nasce a
 * terceira variação de campo.
 *
 * Mesma anatomia do `Field`, de propósito: rótulo sempre visível, erro em texto
 * com símbolo ao lado e `aria-describedby` ligando os dois. Um `textarea` com
 * outra moldura, outro raio e outro estado de foco ao lado de um `Field` é a
 * mesma peça com dois desenhos — o que a decisão 3 do `DESIGN.md` proíbe.
 *
 * `field-sizing-content` deixa a caixa crescer com o que se escreve, até o
 * teto: uma janelinha de três linhas para descrever um problema é o que faz a
 * pessoa escrever menos do que precisava.
 */
export function TextArea({
  label,
  error,
  help,
  counter = false,
  bare = false,
  className,
  id,
  required,
  value,
  maxLength,
  ...props
}: TextAreaProps) {
  const generated = useId();
  const idField = id ?? generated;
  const idError = `${idField}-erro`;
  const idHelp = `${idField}-ajuda`;

  // Ver `Field`: o aviso de obrigatórios do rodapé é contado, não escrito.
  useRequiredMark(required);

  const descricao = [help ? idHelp : null, error ? idError : null]
    .filter(Boolean)
    .join(' ');

  const written = typeof value === 'string' ? value.length : 0;

  return (
    <div className={cn('flex flex-col', bare ? 'gap-0' : 'gap-1.5')}>
      <label
        htmlFor={idField}
        className={cn('text-xs font-medium text-texto', bare && 'sr-only')}
      >
        {label}
        {required && (
          <>
            <span aria-hidden="true" className="ml-0.5 text-erro">
              *
            </span>
            <span className="sr-only"> (obrigatório)</span>
          </>
        )}
      </label>

      <textarea
        {...props}
        id={idField}
        value={value}
        maxLength={maxLength}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={descricao || undefined}
        className={cn(
          /*
           * O CORPO fica em `text-sm`, e não nos 12px que os campos adotaram em
           * 18/09. Aqui não se digita um valor, se escreve prosa — a resposta de
           * uma demanda, o motivo de um cancelamento —, e o `DESIGN.md` põe
           * prosa um degrau acima do cromo da interface. O rótulo desceu junto
           * com os outros, porque ele divide a fileira com eles.
           */
          'w-full bg-superficie text-sm',
          'text-texto placeholder:text-texto-tenue',
          'transition-[border-color,background-color]',
          'field-sizing-content max-h-80',
          'disabled:cursor-not-allowed disabled:bg-superficie-suave disabled:opacity-60',
          bare
            ? // sem moldura e sem anel: quem os desenha é a caixa em volta
              'min-h-20 resize-y border-0 px-3.5 py-3 outline-none'
            : cn(
                'min-h-24 rounded-borda border px-3 py-2',
                error ? 'border-erro' : 'border-borda-forte hover:border-texto-suave',
              ),
          className,
        )}
      />

      <div
        className={cn(
          'flex items-start justify-between gap-3',
          bare && 'px-3.5 pb-1 pt-0',
        )}
      >
        <div className="min-w-0">
          {help && (
            <p id={idHelp} className="text-rotulo text-texto-suave">
              {help}
            </p>
          )}
          {error && (
            <p
              id={idError}
              className="flex items-center gap-1 text-xs font-medium text-erro"
            >
              <Icon nome="atencao" size={16} />
              {error}
            </p>
          )}
        </div>
        {counter && maxLength && (
          /*
           * `aria-hidden`: um contador que se anuncia a cada tecla é ruído
           * insuportável no leitor de tela. Quem precisa do limite o tem no
           * `maxLength`, que o próprio campo impõe.
           */
          <span
            aria-hidden="true"
            className="shrink-0 text-xs tabular-nums text-texto-suave"
          >
            {written}/{maxLength}
          </span>
        )}
      </div>
    </div>
  );
}
